import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { OnboardingItem, DashboardMetrics } from '../types/plms';
import { fetchDashboardMetrics, fetchAllOnboardingItems } from './plmsService';

const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const ITEMS_PER_PAGE = 20;

// Memory cache
interface MemoryCache {
  [key: string]: {
    data: any;
    timestamp: number;
  };
}

const memoryCache: MemoryCache = {};

const MEMORY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Define the cache structure
interface PLMSCache {
  onboardingItems: OnboardingItem[];
  dashboardMetrics: DashboardMetrics;
  lastUpdated: string;
  [key: string]: OnboardingItem[] | DashboardMetrics | string;
}

export type PLMSFilters = {
  section?: string;
  day?: number;
  completionStatus?: 'all' | 'completed' | 'notCompleted';
  includeMasterLists?: boolean;
};

// Cache keys for different data types
const CACHE_KEYS = {
  ALL_ITEMS: 'allItems',
  FILTERED_ITEMS: 'filteredItems',
  DASHBOARD_METRICS: 'dashboardMetrics'
};

const isCacheValid = (lastUpdated: string): boolean => {
  const cacheTime = new Date(lastUpdated).getTime();
  const now = new Date().getTime();
  return now - cacheTime < CACHE_DURATION_MS;
};

const isMemoryCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < MEMORY_CACHE_DURATION;
};

const getMemoryCacheKey = (email: string, key: string): string => {
  return `${email}:${key}`;
};

// Get cache key for filtered items
const getFilteredItemsCacheKey = (filters: PLMSFilters): string => {
  return `${CACHE_KEYS.FILTERED_ITEMS}_${JSON.stringify(filters)}`;
};

// Get paginated items from the cache
const getPaginatedItems = (items: OnboardingItem[], page: number): OnboardingItem[] => {
  const start = page * ITEMS_PER_PAGE;
  return items.slice(start, start + ITEMS_PER_PAGE);
};

const getFromMemoryCache = <T>(email: string, key: string): T | null => {
  const cacheKey = getMemoryCacheKey(email, key);
  const cached = memoryCache[cacheKey];
  
  if (cached && isMemoryCacheValid(cached.timestamp)) {
    
    return cached.data as T;
  }
  
  return null;
};

const setInMemoryCache = <T>(email: string, key: string, data: T): void => {
  const cacheKey = getMemoryCacheKey(email, key);
  memoryCache[cacheKey] = {
    data,
    timestamp: Date.now()
  };
};

export const getCachedData = async (
  email: string,
  workbookId: string,
  filters?: PLMSFilters,
  page?: number
): Promise<{ items: OnboardingItem[]; metrics?: DashboardMetrics; hasMore: boolean }> => {
  const startTime = performance.now();
  console.log(`🔍 [PERF] getCachedData started for ${email}`);
  
  try {
    // Check memory cache first
    const memoryCacheStart = performance.now();
    const memoryCacheKey = filters
      ? getFilteredItemsCacheKey(filters)
      : CACHE_KEYS.ALL_ITEMS;
    
    const memoryData = getFromMemoryCache<OnboardingItem[]>(email, memoryCacheKey);
    const memoryMetrics = getFromMemoryCache<DashboardMetrics>(email, CACHE_KEYS.DASHBOARD_METRICS);
    console.log(`🔍 [PERF] Memory cache check took ${performance.now() - memoryCacheStart}ms`);

    if (memoryData && Array.isArray(memoryData)) {
      console.log(`✅ [PERF] Memory cache HIT - returning ${memoryData.length} items in ${performance.now() - startTime}ms`);
      const paginatedItems = page !== undefined ? getPaginatedItems(memoryData, page) : memoryData;
      const hasMore = page !== undefined ? (page + 1) * ITEMS_PER_PAGE < memoryData.length : false;
      
      return {
        items: paginatedItems,
        metrics: memoryMetrics || undefined,
        hasMore
      };
    }

    // Check Firebase cache
    console.log(`🔍 [PERF] Memory cache MISS - checking Firebase cache`);
    const firebaseStart = performance.now();
    const cacheRef = doc(db, 'plms_cache', email);
    const cacheSnap = await getDoc(cacheRef);
    console.log(`🔍 [PERF] Firebase cache check took ${performance.now() - firebaseStart}ms`);
    let items: OnboardingItem[] = [];
    let metrics: DashboardMetrics | undefined;

    if (cacheSnap.exists()) {
      const cache = cacheSnap.data() as PLMSCache;
      
      if (isCacheValid(cache.lastUpdated)) {
        
        metrics = cache.dashboardMetrics;
        
        // Store metrics in memory cache
        if (metrics) {
          setInMemoryCache(email, CACHE_KEYS.DASHBOARD_METRICS, metrics);
        }
        
        if (filters) {
          items = Array.isArray(cache.onboardingItems) 
            ? filterItems(cache.onboardingItems, filters)
            : [];
          // Store filtered items in memory cache
          setInMemoryCache(email, memoryCacheKey, items);
        } else {
          items = Array.isArray(cache.onboardingItems) 
            ? cache.onboardingItems
            : [];
          // Store all items in memory cache
          setInMemoryCache(email, CACHE_KEYS.ALL_ITEMS, items);
        }
        
        const paginatedItems = page !== undefined ? getPaginatedItems(items, page) : items;
        const hasMore = page !== undefined ? (page + 1) * ITEMS_PER_PAGE < items.length : false;
        
        return {
          items: paginatedItems,
          metrics,
          hasMore
        };
      }
    }

    // Cache miss or invalid - fetch fresh data
    console.log(`❌ [PERF] Firebase cache MISS/INVALID - refreshing cache`);
    const result = await refreshCache(email, workbookId, filters, page);
    console.log(`✅ [PERF] getCachedData completed in ${performance.now() - startTime}ms`);
    return result;
  } catch (error) {
    console.error(`❌ [PERF] getCachedData ERROR after ${performance.now() - startTime}ms:`, error);
    return {
      items: [],
      hasMore: false
    };
  }
};

export const refreshCache = async (
  email: string,
  workbookId: string,
  filters?: PLMSFilters,
  page?: number
): Promise<{ items: OnboardingItem[]; metrics?: DashboardMetrics; hasMore: boolean }> => {
  const startTime = performance.now();
  console.log(`🔄 [PERF] refreshCache started for ${email}, workbookId: ${workbookId}`);
  
  try {
    const fetchStart = performance.now();
    console.log(`🔄 [PERF] Starting parallel fetch of items and metrics`);
    
    const [allItems, metrics] = await Promise.all([
      fetchAllOnboardingItems(workbookId),
      fetchDashboardMetrics(workbookId),
    ]);
    
    console.log(`🔄 [PERF] Parallel fetch completed in ${performance.now() - fetchStart}ms - Items: ${allItems.length}, Metrics: ${Object.keys(metrics).length} keys`);

    // Store all items in Firebase cache
    const cache: PLMSCache = {
      onboardingItems: allItems,
      dashboardMetrics: metrics,
      lastUpdated: new Date().toISOString(),
    };

    // Update Firebase cache
    const cacheUpdateStart = performance.now();
    const cacheRef = doc(db, 'plms_cache', email);
    await setDoc(cacheRef, cache);
    console.log(`🔄 [PERF] Firebase cache update took ${performance.now() - cacheUpdateStart}ms`);

    // Store in memory cache
    setInMemoryCache(email, CACHE_KEYS.ALL_ITEMS, allItems);
    setInMemoryCache(email, CACHE_KEYS.DASHBOARD_METRICS, metrics);

    let items = allItems;
    if (filters) {
      const filterStart = performance.now();
      items = filterItems(allItems, filters);
      console.log(`🔄 [PERF] Filtering took ${performance.now() - filterStart}ms - ${items.length} items after filtering`);
      setInMemoryCache(email, getFilteredItemsCacheKey(filters), items);
    }

    const paginatedItems = page !== undefined ? getPaginatedItems(items, page) : items;
    const hasMore = page !== undefined ? (page + 1) * ITEMS_PER_PAGE < items.length : false;

    console.log(`✅ [PERF] refreshCache completed in ${performance.now() - startTime}ms`);
    return {
      items: paginatedItems,
      metrics,
      hasMore
    };
  } catch (error) {
    console.error(`❌ [PERF] refreshCache ERROR after ${performance.now() - startTime}ms:`, error);
    throw error;
  }
};

export const getCachedFilterOptions = async (
  email: string,
  workbookId: string
): Promise<OnboardingItem[]> => {
  try {
    
    
    // Check memory cache first
    const memoryData = getFromMemoryCache<OnboardingItem[]>(email, CACHE_KEYS.ALL_ITEMS);
    if (memoryData) {
      return memoryData;
    }

    const cacheRef = doc(db, 'plms_cache', email);
    const cacheSnap = await getDoc(cacheRef);

    if (cacheSnap.exists()) {
      const cache = cacheSnap.data() as PLMSCache;
      
      if (isCacheValid(cache.lastUpdated) && Array.isArray(cache.onboardingItems)) {
        // Store in memory cache
        setInMemoryCache(email, CACHE_KEYS.ALL_ITEMS, cache.onboardingItems);
        return cache.onboardingItems;
      }
    }

    // Cache miss or invalid - fetch fresh data
    
    const allItems = await fetchAllOnboardingItems(workbookId);
    
    // Update cache
    const cache: PLMSCache = {
      onboardingItems: allItems,
      dashboardMetrics: (cacheSnap.exists() ? cacheSnap.data().dashboardMetrics : {}) as DashboardMetrics,
      lastUpdated: new Date().toISOString(),
    };

    // Update Firebase cache
    await setDoc(cacheRef, cache);
    
    // Update memory cache
    setInMemoryCache(email, CACHE_KEYS.ALL_ITEMS, allItems);
    
    return allItems;
  } catch (error) {
    
    throw error;
  }
};

const filterItems = (items: OnboardingItem[], filters: PLMSFilters): OnboardingItem[] => {
  let filteredItems = [...items];

  // Filter out Day 0 items unless specifically requested
  if (!filters.includeMasterLists) {
    filteredItems = filteredItems.filter(item => item.day > 0);
  }

  if (filters.section) {
    filteredItems = filteredItems.filter(item => item.section === filters.section);
  }

  if (filters.day !== undefined) {
    filteredItems = filteredItems.filter(item => item.day === filters.day);
  }

  if (filters.completionStatus) {
    switch (filters.completionStatus) {
      case 'completed':
        filteredItems = filteredItems.filter(item => !!item.dateDone);
        break;
      case 'notCompleted':
        filteredItems = filteredItems.filter(item => !item.dateDone);
        break;
    }
  }

  return filteredItems;
};

export const invalidateCache = async (email: string): Promise<void> => {
  try {
    
    
    // Clear memory cache
    Object.keys(memoryCache).forEach(key => {
      if (key.startsWith(email)) {
        delete memoryCache[key];
      }
    });
    
    // Invalidate Firebase cache
    const cacheRef = doc(db, 'plms_cache', email);
    await setDoc(cacheRef, { lastUpdated: new Date(0).toISOString() });
  } catch (error) {
    
    throw error;
  }
};

export const invalidateCacheOnComplete = async (email: string): Promise<void> => {
  try {
    
    await invalidateCache(email);
  } catch (error) {
    
    throw error;
  }
};
